
import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {BoListPanel, IOnOffHandlers} from "bonomo";
import UserChip from "@/components/shared/chips/UserChip.vue";
import {IWebhoook} from "@/model/Webhook/webhoookInterfaces";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import WebhookCreationDialog from "@/components/settings/webhooks/WebhookCreationDialog.vue";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import {EPermissionActions} from "@/model/EPermissionActions";
import {Prop, Watch} from 'vue-property-decorator';
import UserSearch from "@/components/user/widget/UserSearch.vue";
import {IShortUser} from "@/model/shortInterfaces";
import WebhookLogs from "@/components/webhook/WebhookLogs.vue";
import {IAuthUserData} from "@/store/modules/authModule";
import WebhookDetailDialog from "@/components/settings/webhooks/WebhookDetailDialog.vue";
import BoListFilters from "@/components/shared/BoListFilters.vue";
import UserNav from "@/components/user/widget/UserNav.vue";


const authModule = namespace("authModule");
const userModule = namespace("userModule");
const webhookModule = namespace("webhookModule");
const userPermissionModule = namespace("userPermissionModule");

@Component({
  components: {
    BoListFilters,
    BoListPanel,
    UserChip,
    SimpleConfirmationDialog,
    WebhookCreationDialog,
    UserSearch,
    UserNav,
    WebhookLogs,
    WebhookDetailDialog
  }
})
export default class WebhookList extends Vue {
  @Prop({default: 0})
  protected userId?: number;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  @userModule.Getter('user')
  webhookUser!: IShortUser;
  @userModule.Action('fetchUser')
  fetchWebhookUser!: (userId: number) => any;

  @userPermissionModule.Getter('permissionsUser')
  protected permissionUser!: IAuthUserData;

  @webhookModule.Getter('webhookList')
  protected webhooks!: IWebhoook[];
  @webhookModule.Action('fetchWebhooks')
  protected fetchWebhooks!: (param: object) => any;
  @webhookModule.Action('fetchWebhook')
  protected fetchWebhook!: (webhookId: number) => any;
  @webhookModule.Action('fetchUserWebhooks')
  protected fetchUserWebhooks!: (param: object) => any;
  @webhookModule.Action('deleteWebhook')
  protected deleteWebhook!: (id: number) => any;
  @webhookModule.Action('clearWebhooks')
  protected clearWebhooks!: () => any;

  get isSelf() {
    return '' + this.$route.params.userId === '' + this.authUser.id;
  }

  @Watch('$route')
  onRouteChanged() {
    this.searchValue = '';
    // I have to do this on nav change too as mounted() is not called when only route params change. This includes also
    //    change from users/webhooks to users/123/webhooks. But, this watch will catch it too.
    this.guardNav();
  }

  private webhookToDelete: IWebhoook|null = null;
  private showDeleteDialog = false;
  private showCreateDialog = false;
  private disableRemove = false;
  private searchValue:string = ''

  private subscribes: IOnOffHandlers = {};

  private showDetailDialog = false;
  private detailDialogLoading: boolean = false;

  // We will hide the content by default. This will prevent bonomo from loading a possibly invalid source. Happens when
  //  we enter without userId on webhooks/userId and get "forwarded" to users/{myId}/webhooks
  private showing: boolean = false;

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.webhookLoaded': this.onWebhookLoadedOK,
      'backend.call.KO.webhookLoaded': this.onWebhookLoadedKO,
    })
  }

  public mounted() {
    this.guardNav();
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected fetchWebhookList(filters: any) {
    if (this.userId) {
      this.fetchUserWebhooks({userId: this.userId, filters});
      this.fetchWebhookUser(this.userId);
    } else {
      this.fetchWebhooks(filters);
    }
  }

  protected get existsUserId() {
    return this.userId && this.userId > 0;
  }

  protected get filters() {
    return {search: this.searchValue};
  }

  protected get remoteFilters() {
    return this.userIsAdmin ? ['search'] : []
  }

  protected get userIsAdmin() {
    return this.$canAction(EPermissionActions.ACTION_WEBHOOK_ADMIN);
  }

  protected get headers(): BetterDataTableHeader[] {
    let ret: BetterDataTableHeader[] =  [
      // { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('common.user'), align: 'start', sortable: false, value: 'user' },
      { text: this.$t('common.url'), align: 'start', sortable: false, value: 'url' },
      { text: this.$t('webhooks.action'), align: 'start', sortable: false, value: 'action' },
      { text: this.$t('webhooks.created_by'), align: 'start', sortable: false, value: 'createdBy' },
      {text: "", sortable: false, value: "details", align: "center"},
      { text: "", sortable: false, value: "controls", align: "center"},
    ];
    if (this.existsUserId && (this.userId == this.authUser.id)) {
      delete ret[1];
    }
    return ret;
  }

  private guardNav() {
    this.showing = false;
    if (!this.$canAction(EPermissionActions.ACTION_WEBHOOK_ADMIN) &&
        (!this.existsUserId || this.userId != this.authUser.id)
    ) {
      if (this.$canAction(EPermissionActions.ACTION_ORDERS_WEBHOOK ||
          this.$canAction(EPermissionActions.ACTION_USERS_WEBHOOK)) &&
          this.authUser && this.authUser.id
      ) {
        this.$router.push({name: 'userWebhooks', params: {userId: ''+this.authUser.id}});
      }
      else {
        this.$router.push({name: 'home'});
      }
    }
    else {
      this.showing = true;
    }
  }

  protected onDeleteWebhookBtn(webhook: IWebhoook) {
    this.webhookToDelete = webhook;
    this.showDeleteDialog = true;
  }

  private onAcceptDeleteWebhookBtn() {
    if (this.webhookToDelete) {
      this.deleteWebhook(this.webhookToDelete.id);
    }
    this.showDeleteDialog = false;
  }

  private onOpenCreateDialog() {
    this.showCreateDialog = true;
  }

  private onGotoUser(user: IShortUser) {
    if (user.id !== parseInt(this.$route.params.userId)) {
      this.$router.push({
        name: 'userWebhooks',
        params: {
          userId: `${ user.id }`
        }
      });
    }
  }

  private onShowDetailDialog(webhook: IWebhoook) {
    this.showDetailDialog = true;
    this.detailDialogLoading = true;
    this.fetchWebhook(webhook.id)
  }

  private onWebhookLoadedOK() {
    this.detailDialogLoading = false;
  }

  private onWebhookLoadedKO() {
    this.showDetailDialog = false;
  }

}

